import React, {useEffect, useState} from 'react'
import AppLayout from '../../components/Layouts/AppLayout'
import {useTranslation} from 'react-i18next'
import {useStateContext} from '../../context/ContextProvider';
import {useAuth} from '../../hooks/auth';
import { Divider, List, ListItemButton, styled } from '@mui/material';
import ReportModal from '../../components/reports/ReportModal';
import axios from '../../lib/axios';
import { REPORT_CODES } from '../../constants/constants'
import Loading from '../../components/Loading';
import { isNil } from 'lodash';
import { SnackbarProvider, enqueueSnackbar, closeSnackbar } from 'notistack';
import ClearIcon from "@mui/icons-material/Clear"

const CATEGORY_STYLES = {
    collection: {
        color: '#b7472a',
        fontSize: '22px',
        transform: 'rotate(225deg)'
    },
    shipment: {
        transform: 'rotate(45deg)',
        color: '#336195',
        fontSize: '22px'
    },
    activity: {
        color: '#4caf50',
        fontSize: '22px'
    },
    production: {
        color: '#795548',
        fontSize: '22px'
    },
    payroll: {
        color: '#4caf50',
        fontSize: '22px'
    }
}
const CATEGORY_ICONS = {
    collection: 'fa-arrow-up-long',
    shipment: 'fa-arrow-up-long',
    activity: 'fa-chart-line',
    production: 'fa-hammer',
    payroll: 'fa-money-bill-wave'
}

const CustomListItemButton = styled(ListItemButton)({
    paddingTop: "14px !important",
    paddingBottom: "14px !important",
    paddingLeft: "20px !important"
})

const Reports = () => {
    const { t } = useTranslation()
    const { config, company_id, choosesite, allSites, reportCategories } = useStateContext()
    const { user } = useAuth({ middleware: 'guest' })

    /* State(s) */
    const [isLoading, setIsLoading] = useState(false)
    const [loadingProgress, setLoadingProgress] = useState(0)

    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([])
    const [customers, setCustomers] = useState([])
    const [drivers, setDrivers] = useState([])
    const [forklifters, setForklifters] = useState([])
    const [users, setUsers] = useState([])
    const [productionTypes, setProductionTypes] = useState([])
    const [productionEmployees, setProductionEmployees] = useState([])

    const [reportCode, setReportCode] = useState('')
    const [open, setOpen] = useState(false)

    /* Function(s) */
    const handleOpen = (rc = 'default') => {
        setReportCode(rc)
        setOpen(true)
    }
    const handleClose = () =>  {
        setOpen(false)
        setVendors([])
        setCustomers([])
        setDrivers([])
        setForklifters([])
        setUsers([])
        setProductionTypes([])
        setProductionEmployees([])
    }

    const getVendors = async (company_id) => {
        setIsLoading(true)

        await axios.get(`/api/vendors?with=purchase_addresses&company_id=${company_id}`, config)
            .then(res => {
                const vendors = res.data
                setVendors(vendors)
            })
            .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('vendors')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getCustomers = async (company_id) => {
        setIsLoading(true)

        await axios.get(`/api/customers?with=ship_addresses&company_id=${company_id}`, config)
            .then(res => {
                const customers = res.data
                setCustomers(customers)
            })
            .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('customers')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getDrivers = async (site_id) => {
        setIsLoading(true)

        await axios.get(`/api/users?role=driver&site_id=${site_id}`, config)
            .then(res => {
                const drivers = res.data.data
                setDrivers(drivers)
           })
           .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('drivers')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getForklifters = async (site_id) => {
        setIsLoading(true)

        await axios.get(`/api/users?role=forklift_driver&site_id=${site_id}`, config)
            .then(res => {
                const forklifters = res.data.data
                setForklifters(forklifters)
            })
            .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('forklift_drivers')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getUsers = async (company_id) => {
        setIsLoading(true)

        await axios.get(`/api/users`, config)
            .then(res => {
                const users = res.data.data
                const filteredUsers = (user?.role != 'master_admin')
                    ? users.filter((user) => (user.role_slug != 'master_admin') && (Number(user.company_id) == Number(company_id)))
                    : users
                setUsers(filteredUsers)
            })
            .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('users')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getProductionTypes = async (company_id) => {
        setIsLoading(true)

        await axios.get(`/api/production-types?with=items&company_id=${company_id}`, config)
            .then(res => {
                const productionTypes = res.data
                setProductionTypes(productionTypes)
            })
            .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('production_types')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getProductionEmployees = async (site_id) => {
        setIsLoading(true)

        await axios.get(`/api/production-employees?site=${site_id}`, config)
            .then(res => {
                const productionEmployees = res.data
                setProductionEmployees(productionEmployees)
            })
            .catch(({response}) => {
                enqueueSnackbar(
                    t('could_not_x_y', {
                        x: t('get'),
                        y: t('production_employees')
                    })
                )
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    useEffect(() => {
        setCategories(reportCategories.data)
    }, [reportCategories])

    return (
        <>
            {(isLoading || reportCategories.isLoading) ? <Loading/> : ''}

            <SnackbarProvider
                maxSnack={3}
                /* preventDuplicate */
                autoHideDuration={12000}
                variant='error'
                action={(snackbarId) => (
                    <button onClick={() => closeSnackbar(snackbarId)}>
                        <ClearIcon />
                    </button>
                )}
            />
            <AppLayout>
                <div className='p-5 flex flex-row flex-wrap'>
                    {categories.map((category) => (
                        <div className='p-3 basis-full xl:basis-1/4 lg:basis-1/3 md:basis-1/2' key={`category-${category.id}`}>
                            <div className='bg-white mb-2 rounded-md'>
                                <div className='p-5 flex gap-3 justify-between items-center'>
                                    <p className='font-bold roboto color-fake'>{t('x_reports', {x: t(category.name)})}</p>
                                    <span style={CATEGORY_STYLES[category.name]} className="flex items-center justify-center">
                                        <i className={`fa-solid ${CATEGORY_ICONS[category.name]}`}></i>
                                    </span>
                                </div>

                                <Divider />

                                <List aria-label={`${category.name} reports`}>
                                    {category.reports.length > 0 && category.reports.filter((report) => report.is_active).map((report) => (
                                        <CustomListItemButton
                                            key={`category-${category.id}-report-${report.id}`}
                                            component="a"
                                            onClick={() => {
                                                handleOpen(report.name);

                                                switch(report.name) {
                                                    case REPORT_CODES.collections.COMPLETED_COLLECTIONS_BY_VENDOR:
                                                    case REPORT_CODES.collections.ONTIME_COLLECTIONS:
                                                    case REPORT_CODES.activities.TRAILER_HISTORY_SUMMARY_BY_VENDOR:
                                                        getVendors(localStorage.getItem('company_id'));
                                                        break;
                                                    case REPORT_CODES.shipments.ONTIME_DELIVERIES:
                                                    case REPORT_CODES.activities.TRAILER_HISTORY_SUMMARY_BY_CUSTOMER:
                                                        getCustomers(localStorage.getItem('company_id'));
                                                        break;
                                                    case REPORT_CODES.activities.DRIVER_ACTIVITY_REPORT:
                                                        getDrivers(localStorage.getItem('site'));
                                                        break;
                                                    case REPORT_CODES.activities.FORKLIFT_DRIVER_ACTIVITY_REPORT:
                                                        getForklifters(localStorage.getItem('site'));
                                                        break;
                                                    case REPORT_CODES.activities.SYSTEM_USAGE_BY_USER:
                                                        getUsers(localStorage.getItem('company_id'));
                                                        break;
                                                    case REPORT_CODES.production.PRODUCTION_ACTIVITY_BY_DAY:
                                                        getProductionTypes(localStorage.getItem('company_id'));
                                                        break;
                                                    case REPORT_CODES.production.PRODUCTION_EMPLOYEE_ACTIVITY_BY_DAY:
                                                    case REPORT_CODES.payroll.PAYROLL_REPORT_BY_PRODUCTION_EMPLOYEE:
                                                        getProductionEmployees(localStorage.getItem('site'));
                                                        break;
                                                    default:
                                                }
                                            }}
                                            disabled={isLoading}
                                        >
                                            {t(`${category.name}_reports.${report.name}`)}
                                        </CustomListItemButton>
                                    ))}
                                </List>
                            </div>
                        </div>
                    ))}
                </div>
            </AppLayout>

            <ReportModal
                open={open}
                setOpen={setOpen}
                reportCode={reportCode}
                companyVendors={vendors}
                companyCustomers={customers}
                companyDrivers={drivers}
                companyForklifters={forklifters}
                companyUsers={users}
                companySites={allSites}
                companyProductionTypes={productionTypes}
                companyProductionEmployees={productionEmployees}
                onClose={handleClose}
                setIsLoading={setIsLoading}
            />
        </>
    )
}

export default Reports

